<script setup lang="ts">
/**
 * Full-screen mobile overlay for a service type detail view.
 *
 * Rendered as a fixed-position layer (z-index 10, above MobileBottomNav's 5)
 * via Teleport. iOS-style navbar provides back navigation and an Edit button.
 *
 * All four data streams — type detail, stats, service occurrences, category
 * names, and performers — are auto-drained upfront so the history div list and
 * sparklines always reflect the complete dataset (mirrors the desktop card).
 *
 * Edit opens AddServiceTypeForm via useAddServiceTypeFormStore. The form
 * must be mounted above this screen (z-index ≥ 11) by the parent to overlay
 * it cleanly without dismissing the card first.
 */
import { computed, ref, toRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import MonthlyBarChart from '@/components/stats/MonthlyBarChart.vue'
import DeleteConfirmSheet from '@/components/main/DeleteConfirmSheet.vue'
import {
  deleteBusinessServiceType,
  fetchBusinessServiceType,
  fetchBusinessServiceTypeStats,
  businessServiceTypePriceAmount,
  type BusinessServiceTypeDetail,
  type BusinessServiceTypeStats,
} from '@/api/businessServiceTypes'
import { fetchBusinessServiceCategories } from '@/api/businessServiceCategories'
import { fetchPerformers, type Performer } from '@/api/performers'
import { fetchClientsByIds } from '@/api/clients'
import { genderBucket, type GenderBucket } from '@/utils/gender'
import {
  fetchBusinessServicesByType,
  type BusinessServicesByTypeResponse,
  type BusinessService,
} from '@/api/businessServices'
import { businessServiceTypeCardPollingInterval } from '@/config/api'
import { formatDateTime } from '@/utils/formatDateTime'
import { formatDuration } from '@/utils/formatDuration'
import { formatStartDateFromIso } from '@/utils/formatStartDate'
import { defaultLocaleForDate } from '@/config/date'
import { formatMoney, currencySymbol } from '@/utils/formatMoney'
import { useAddServiceTypeFormStore } from '@/stores/add_service_type_form_store'
import { usePermissions } from '@/composables/usePermissions'
import { ResourceAccessRole } from '@/models/resource_access_role'

const props = defineProps<{
  serviceTypeId: string
}>()

const emit = defineEmits<{
  close: []
}>()

const serviceTypeId = toRef(props, 'serviceTypeId')
const { t } = useI18n()
const { hasRole } = usePermissions()
const canEdit = computed(() => hasRole(ResourceAccessRole.BusinessServiceTypesEdit))
const canDelete = computed(() => hasRole(ResourceAccessRole.BusinessServiceTypesDelete))

const isEnabled = computed(
  () => typeof serviceTypeId.value === 'string' && serviceTypeId.value.length > 0,
)

// ── Queries ───────────────────────────────────────────────────────────────────
const typeQuery = useQuery({
  queryKey: ['businessServiceType', serviceTypeId],
  queryFn: () => fetchBusinessServiceType(serviceTypeId.value),
  enabled: isEnabled,
  staleTime: 0,
  refetchOnMount: 'always',
  refetchInterval: businessServiceTypeCardPollingInterval,
  refetchIntervalInBackground: false,
})

const statsQuery = useQuery({
  queryKey: ['businessServiceTypeStats', serviceTypeId],
  queryFn: () => fetchBusinessServiceTypeStats(serviceTypeId.value),
  enabled: isEnabled,
  staleTime: 0,
  refetchOnMount: 'always',
  refetchInterval: businessServiceTypeCardPollingInterval,
  refetchIntervalInBackground: false,
})

const servicesQuery = useInfiniteQuery({
  queryKey: ['businessServicesByType', serviceTypeId],
  queryFn: ({ pageParam }) =>
    fetchBusinessServicesByType(serviceTypeId.value, pageParam as number | undefined),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage: BusinessServicesByTypeResponse) =>
    lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
  enabled: isEnabled,
  staleTime: 0,
  refetchOnMount: 'always',
  refetchInterval: businessServiceTypeCardPollingInterval,
  refetchIntervalInBackground: false,
})

const categoryNamesQuery = useInfiniteQuery({
  queryKey: ['businessServiceCategories'],
  queryFn: ({ pageParam }) => fetchBusinessServiceCategories(pageParam as number | undefined),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) =>
    lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
  staleTime: 0,
  refetchOnMount: 'always',
  refetchInterval: businessServiceTypeCardPollingInterval,
  refetchIntervalInBackground: false,
})

const performersQuery = useInfiniteQuery({
  queryKey: ['performers'],
  queryFn: ({ pageParam }) => fetchPerformers(pageParam as number | undefined),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) =>
    lastPage.has_next_page && lastPage.next_cursor != null ? lastPage.next_cursor : undefined,
  staleTime: 0,
  refetchOnMount: 'always',
  refetchInterval: businessServiceTypeCardPollingInterval,
  refetchIntervalInBackground: false,
})

watch(
  () => ({
    hasNext: categoryNamesQuery.hasNextPage.value === true,
    fetching: categoryNamesQuery.isFetchingNextPage.value === true,
  }),
  ({ hasNext, fetching }) => {
    if (hasNext && !fetching) void categoryNamesQuery.fetchNextPage()
  },
  { immediate: true, flush: 'post' },
)

watch(
  () => ({
    hasNext: performersQuery.hasNextPage.value === true,
    fetching: performersQuery.isFetchingNextPage.value === true,
  }),
  ({ hasNext, fetching }) => {
    if (hasNext && !fetching) void performersQuery.fetchNextPage()
  },
  { immediate: true, flush: 'post' },
)

watch(
  () => ({
    hasNext: servicesQuery.hasNextPage.value === true,
    fetching: servicesQuery.isFetchingNextPage.value === true,
  }),
  ({ hasNext, fetching }) => {
    if (hasNext && !fetching) void servicesQuery.fetchNextPage()
  },
  { immediate: true, flush: 'post' },
)

// ── Derived data ──────────────────────────────────────────────────────────────
const type = computed<BusinessServiceTypeDetail | null>(() => typeQuery.data.value ?? null)
const stats = computed<BusinessServiceTypeStats | null>(() => statsQuery.data.value ?? null)

const categoryNameById = computed<Map<string, string>>(() => {
  const map = new Map<string, string>()
  for (const page of categoryNamesQuery.data.value?.pages ?? []) {
    for (const item of page.items ?? []) {
      map.set(item.id, item.name)
    }
  }
  return map
})

const categoryName = computed<string | null>(() => {
  const svc = type.value
  if (!svc) return null
  const name = categoryNameById.value.get(svc.business_service_name_id)
  return typeof name === 'string' && name.trim() !== '' ? name.trim() : null
})

const typeName = computed<string>(() => {
  const svc = type.value
  if (!svc) return ''
  const desc = typeof svc.description === 'string' ? svc.description.trim() : ''
  if (desc.length > 0) return desc
  if (categoryName.value) return categoryName.value
  return t('tabs.services.serviceTypeFallback', { id: svc.id })
})

const durationLabel = computed<string>(() => formatDuration(type.value?.duration) ?? '')

const priceLabel = computed<string | null>(() => {
  const svc = type.value
  if (!svc) return null
  const amount = businessServiceTypePriceAmount(svc)
  if (amount === undefined) return null
  return formatMoney({ amount, precision: 0, fractionDigits: 2 }).formatted
})

const sinceParts = computed(() => formatStartDateFromIso(stats.value?.first_service_date_time))

const servicesTotalLabel = computed<string | null>(() => {
  const v = stats.value?.business_service_count
  if (typeof v !== 'number' || !Number.isFinite(v)) return null
  return v.toLocaleString()
})

const clientVisitsLabel = computed<string | null>(() => {
  const v = stats.value?.client_visit_count
  if (typeof v !== 'number' || !Number.isFinite(v)) return null
  return v.toLocaleString()
})

const uniqueClientsLabel = computed<string | null>(() => {
  const v = stats.value?.unique_client_count
  if (typeof v !== 'number' || !Number.isFinite(v)) return null
  return v.toLocaleString()
})

const visitsPerClientLabel = computed<string | null>(() => {
  const v = stats.value?.average_visits_per_client
  if (typeof v !== 'number' || !Number.isFinite(v)) return null
  return v.toFixed(1)
})

// ── Occurrences ───────────────────────────────────────────────────────────────
const occurrences = computed<BusinessService[]>(() => {
  const out: BusinessService[] = []
  for (const page of servicesQuery.data.value?.pages ?? []) {
    for (const s of page.items ?? []) out.push(s)
  }
  return out
})

// ── Clients (for the history rows' gender breakdown) ─────────────────────────
const allClientIds = computed<string[]>(() => {
  const ids = new Set<string>()
  for (const s of occurrences.value) {
    if (Array.isArray(s.client_ids)) for (const id of s.client_ids) ids.add(id)
  }
  return Array.from(ids).sort()
})

const clientsQuery = useQuery({
  queryKey: ['clientsByIds', allClientIds],
  queryFn: () => fetchClientsByIds(allClientIds.value),
  enabled: computed(() => allClientIds.value.length > 0),
  staleTime: 0,
  refetchInterval: businessServiceTypeCardPollingInterval,
  refetchIntervalInBackground: false,
})

const clientGenderById = computed<Map<string, GenderBucket | null>>(() => {
  const map = new Map<string, GenderBucket | null>()
  for (const c of clientsQuery.data.value?.items ?? []) {
    map.set(c.id, genderBucket(c.gender))
  }
  return map
})

// ── Sparklines ────────────────────────────────────────────────────────────────
const sparkMonthCount = computed<number>(() => {
  const iso = stats.value?.first_service_date_time
  if (typeof iso !== 'string' || iso.trim() === '') return 12
  const start = new Date(iso)
  if (Number.isNaN(start.getTime())) return 12
  const now = new Date()
  const diff =
    (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth()) + 1
  if (!Number.isFinite(diff) || diff <= 0) return 1
  return Math.min(diff, 120)
})

function monthlyBuckets(
  services: BusinessService[],
  months: number,
  weight: (s: BusinessService) => number,
): number[] {
  const now = new Date()
  const nowYear = now.getFullYear()
  const nowMonth = now.getMonth()
  const bins = new Array<number>(months).fill(0)
  for (const s of services) {
    const d = new Date(s.date_time)
    if (Number.isNaN(d.getTime())) continue
    const diff = (nowYear - d.getFullYear()) * 12 + (nowMonth - d.getMonth())
    if (diff >= 0 && diff < months) bins[months - 1 - diff]! += weight(s)
  }
  return bins
}

const sessionBuckets = computed<number[]>(() =>
  monthlyBuckets(occurrences.value, sparkMonthCount.value, () => 1),
)

const clientVisitBuckets = computed<number[]>(() =>
  monthlyBuckets(occurrences.value, sparkMonthCount.value, (s) =>
    Array.isArray(s.client_ids) ? s.client_ids.length : 0,
  ),
)

const uniqueClientsBuckets = computed<number[]>(() => {
  const months = sparkMonthCount.value
  const now = new Date()
  const nowYear = now.getFullYear()
  const nowMonth = now.getMonth()
  const setsPerMonth: Set<string>[] = Array.from({ length: months }, () => new Set<string>())
  for (const s of occurrences.value) {
    const d = new Date(s.date_time)
    if (Number.isNaN(d.getTime())) continue
    const diff = (nowYear - d.getFullYear()) * 12 + (nowMonth - d.getMonth())
    if (diff >= 0 && diff < months) {
      const bucket = setsPerMonth[months - 1 - diff]
      if (!bucket || !Array.isArray(s.client_ids)) continue
      for (const id of s.client_ids) bucket.add(id)
    }
  }
  return setsPerMonth.map((set) => set.size)
})

const servicesFullyLoaded = computed<boolean>(
  () =>
    servicesQuery.hasNextPage.value !== true &&
    servicesQuery.isFetchingNextPage.value === false &&
    (servicesQuery.data.value?.pages?.length ?? 0) > 0,
)

const servicesLoadingDone = computed<boolean>(
  () =>
    servicesQuery.hasNextPage.value !== true &&
    servicesQuery.isFetchingNextPage.value === false &&
    !servicesQuery.isPending.value,
)

const attendanceTotal = computed<number>(() => {
  let total = 0
  for (const s of occurrences.value) {
    if (Array.isArray(s.client_ids)) total += s.client_ids.length
  }
  return total
})

const clientsPerServiceLabel = computed<string | null>(() => {
  if (!servicesFullyLoaded.value) return null
  const services = occurrences.value.length
  if (services === 0) return null
  return (attendanceTotal.value / services).toFixed(1)
})

const clientsPerServiceBuckets = computed<number[]>(() => {
  const sessions = sessionBuckets.value
  const visits = clientVisitBuckets.value
  const months = sparkMonthCount.value
  const out = new Array<number>(months).fill(0)
  for (let i = 0; i < months; i++) {
    const s = sessions[i] ?? 0
    const v = visits[i] ?? 0
    if (s > 0) out[i] = v / s
  }
  return out
})

const sparkLabels = computed<string[]>(() => {
  const months = sparkMonthCount.value
  const now = new Date()
  const showYear = months > 12
  const fmt = new Intl.DateTimeFormat(
    defaultLocaleForDate.value,
    showYear ? { month: 'short', year: '2-digit' } : { month: 'short' },
  )
  const labels: string[] = []
  for (let i = 0; i < months; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - (months - 1 - i), 1)
    labels.push(fmt.format(d))
  }
  return labels
})

const sparkTooltipLabels = computed<string[]>(() => {
  const months = sparkMonthCount.value
  const now = new Date()
  const fmt = new Intl.DateTimeFormat(defaultLocaleForDate.value, { month: 'short' })
  const labels: string[] = []
  for (let i = 0; i < months; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - (months - 1 - i), 1)
    labels.push(fmt.format(d))
  }
  return labels
})

const sparkLabelStride = computed<number>(() => Math.max(1, Math.ceil(sparkMonthCount.value / 4)))

// ── Performers ────────────────────────────────────────────────────────────────

interface PerformerDisplay {
  id: string
  shortName: string
  gender: GenderBucket
}

const performerById = computed<Map<string, Performer>>(() => {
  const map = new Map<string, Performer>()
  for (const page of performersQuery.data.value?.pages ?? []) {
    for (const p of page.items ?? []) map.set(p.id, p)
  }
  return map
})

function shortenPerformerName(name: string | null | undefined, id: string): string {
  const trimmed = typeof name === 'string' ? name.trim() : ''
  if (trimmed === '') return t('tabs.services.performerFallback', { id })
  const parts = trimmed.split(/\s+/)
  if (parts.length < 2) return trimmed
  const [lastName, firstName] = parts
  if (!lastName || !firstName) return trimmed
  return `${firstName[0]}. ${lastName}`
}

function resolvePerformers(ids: string[] | null | undefined): PerformerDisplay[] {
  if (!Array.isArray(ids)) return []
  return ids.map((id) => {
    const p = performerById.value.get(id)
    return {
      id,
      shortName: shortenPerformerName(p?.name, id),
      gender: genderBucket(p?.gender) ?? 'men',
    }
  })
}

// ── History rows ──────────────────────────────────────────────────────────────
interface HistoryRow {
  // One formatted row in the service history list.

  kind: 'row'
  // Discriminant against `HistoryDateHeader` in `historyListItems`'s union.

  id: string
  date: string
  time: string
  performers: PerformerDisplay[]
  clientsCount: number
  menCount: number
  womenCount: number
  revenueLabel: string | null
}

interface HistoryDateHeader {
  // Synthetic sticky divider spliced into `historyListItems` ahead of each
  // date's first occurrence — the div-list equivalent of `DateHeaderRowCell`
  // (which only works inside a `<table>`'s row/`<td>` structure).

  kind: 'date-header'
  id: string
  date: string
  count: number
}

type HistoryListItem = HistoryRow | HistoryDateHeader

const historyRows = computed<HistoryRow[]>(() =>
  occurrences.value.map((s) => {
    const { formattedDate, formattedTime } = formatDateTime({
      date: s.date_time,
      dateStyle: 'medium',
    })
    const rawRevenue = typeof s.price === 'number' && Number.isFinite(s.price) ? s.price : null
    const clientIds = Array.isArray(s.client_ids) ? s.client_ids : []
    let menCount = 0
    let womenCount = 0
    for (const id of clientIds) {
      const gender = clientGenderById.value.get(id)
      if (gender === 'men') menCount++
      else if (gender === 'women') womenCount++
    }
    return {
      kind: 'row',
      id: s.id,
      date: formattedDate,
      time: formattedTime,
      performers: resolvePerformers(s.performers_ids),
      clientsCount: clientIds.length,
      menCount,
      womenCount,
      revenueLabel:
        rawRevenue !== null
          ? formatMoney({ amount: rawRevenue, precision: 0, fractionDigits: 0 }).formatted
          : null,
    }
  }),
)

/**
 * `historyRows` with a synthetic `date-header` item spliced in ahead of each
 * date's first occurrence. `fetchBusinessServicesByType` returns occurrences
 * descending by `date_time`, so same-date rows are already contiguous — a
 * single pass comparing against the previous row's date is enough (mirrors
 * `ServicesTab`'s `mobileData`).
 */
const historyListItems = computed<HistoryListItem[]>(() => {
  const result: HistoryListItem[] = []
  let currentHeader: HistoryDateHeader | null = null
  for (const row of historyRows.value) {
    if (!currentHeader || currentHeader.date !== row.date) {
      currentHeader = {
        kind: 'date-header',
        id: `date-header:${row.date}`,
        date: row.date,
        count: 0,
      }
      result.push(currentHeader)
    }
    currentHeader.count += 1
    result.push(row)
  }
  return result
})

// ── Edit ──────────────────────────────────────────────────────────────────────
const addServiceTypeFormStore = useAddServiceTypeFormStore()

function openEdit(): void {
  const svc = type.value
  if (!svc) return
  const priceAmount = businessServiceTypePriceAmount(svc)
  addServiceTypeFormStore.openForEdit(svc.id, {
    serviceCategoryId: svc.business_service_name_id,
    description: svc.description ?? '',
    duration: svc.duration ?? 'PT1H',
    price: priceAmount ?? null,
    priceStart: svc.price?.start ?? new Date().toISOString(),
  })
}

// ── Delete ────────────────────────────────────────────────────────────────────
const queryClient = useQueryClient()

const showDeleteConfirm = ref(false)

const deleteMutation = useMutation({
  mutationFn: () => deleteBusinessServiceType(serviceTypeId.value),
  onSuccess() {
    void queryClient.invalidateQueries({ queryKey: ['businessServiceTypes'] })
    showDeleteConfirm.value = false
    emit('close')
  },
})

const deleteDescription = computed<string>(() =>
  t('cards.businessServiceType.confirmDeleteDescription', { type: typeName.value }),
)

function handleDelete(): void {
  if (!type.value) return
  showDeleteConfirm.value = true
}

function confirmDelete(): void {
  deleteMutation.mutate()
}

function cancelDelete(): void {
  showDeleteConfirm.value = false
}
</script>

<template>
  <Teleport to="body">
    <div class="stm-screen font-tufte text-tufte-text">
      <!-- iOS navbar -->
      <nav class="stm-navbar">
        <button type="button" class="stm-nav-back" @click="emit('close')">
          <svg
            width="8"
            height="14"
            viewBox="0 0 8 14"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M7 1L1 7L7 13" />
          </svg>
          {{ t('cards.businessServiceType.backLabel') }}
        </button>
        <span class="stm-nav-title">{{ t('cards.businessServiceType.navTitle') }}</span>
        <button
          v-if="canEdit"
          type="button"
          class="stm-nav-edit"
          :disabled="!type"
          @click="openEdit"
        >
          {{ t('common.edit') }}
        </button>
      </nav>

      <!-- Loading / error -->
      <div v-if="typeQuery.isPending.value && !type" class="stm-state">
        {{ t('common.loadingShort') }}
      </div>
      <div v-else-if="typeQuery.isError.value && !type" class="stm-state stm-state--error">
        {{ t('cards.businessServiceType.loadErrorShort') }}
      </div>

      <!-- Content -->
      <div v-else-if="type" class="stm-scroll">
        <!-- Header: name + duration / price -->
        <div class="stm-header">
          <div class="stm-header-left">
            <div class="stm-name">{{ typeName }}</div>
            <div v-if="durationLabel" class="stm-duration">
              {{ durationLabel }} {{ t('cards.businessServiceType.sessionSuffix') }}
            </div>
          </div>
          <div v-if="priceLabel" class="stm-price">
            <div class="stm-price-amt">
              {{ priceLabel }}<span class="stm-price-cur">{{ currencySymbol }}</span>
            </div>
            <div class="stm-price-per">{{ t('cards.businessServiceType.perClient') }}</div>
          </div>
        </div>

        <!-- Stat grid: 3 cells -->
        <div class="stm-stat-grid">
          <div class="stm-stat-cell">
            <div class="stm-stat-num">{{ servicesTotalLabel ?? '…' }}</div>
            <div class="stm-stat-cap">{{ t('cards.businessServiceType.statServices') }}</div>
          </div>
          <div class="stm-stat-cell">
            <div class="stm-stat-num">{{ clientVisitsLabel ?? '…' }}</div>
            <div class="stm-stat-cap">{{ t('cards.businessServiceType.clientVisits') }}</div>
          </div>
          <div class="stm-stat-cell">
            <div class="stm-stat-num stm-stat-num--stale">{{ visitsPerClientLabel ?? '…' }}</div>
            <div class="stm-stat-cap">{{ t('cards.businessServiceType.visitsPerClient') }}</div>
          </div>
        </div>

        <!-- Meta strip -->
        <div class="stm-meta-strip">
          <div class="stm-meta-line">
            <span class="stm-meta-label">{{ t('common.since') }}</span>
            <span class="stm-meta-val stm-meta-val--pos">
              <template v-if="sinceParts.formattedDate">
                {{ sinceParts.formattedDate
                }}<template v-if="sinceParts.durationSince">
                  · {{ sinceParts.durationSince }}</template
                >
              </template>
              <span v-else class="text-tufte-muted">–</span>
            </span>
          </div>
          <div class="stm-meta-line">
            <span class="stm-meta-label">{{ t('cards.businessServiceType.servicesTotal') }}</span>
            <span class="stm-meta-val">
              <template v-if="servicesTotalLabel !== null">{{ servicesTotalLabel }}</template>
              <span v-else class="text-tufte-muted">…</span>
            </span>
          </div>
          <div class="stm-meta-line">
            <span class="stm-meta-label">{{ t('cards.businessServiceType.clientVisits') }}</span>
            <span class="stm-meta-val">
              <template v-if="clientVisitsLabel !== null">{{ clientVisitsLabel }}</template>
              <span v-else class="text-tufte-muted">…</span>
            </span>
          </div>
          <div class="stm-meta-line">
            <span class="stm-meta-label">{{ t('common.uniqueClients') }}</span>
            <span class="stm-meta-val">
              <template v-if="uniqueClientsLabel !== null">{{ uniqueClientsLabel }}</template>
              <span v-else class="text-tufte-muted">…</span>
            </span>
          </div>
          <div class="stm-meta-line">
            <span class="stm-meta-label">{{
              t('cards.businessServiceType.visitsPerClient')
            }}</span>
            <span class="stm-meta-val stm-meta-val--stale">
              <template v-if="visitsPerClientLabel !== null">{{ visitsPerClientLabel }}</template>
              <span v-else class="text-tufte-muted">…</span>
            </span>
          </div>
          <div class="stm-meta-line">
            <span class="stm-meta-label">{{
              t('cards.businessServiceType.clientsPerService')
            }}</span>
            <span class="stm-meta-val stm-meta-val--stale">
              <template v-if="clientsPerServiceLabel !== null">{{
                clientsPerServiceLabel
              }}</template>
              <span v-else class="text-tufte-muted">…</span>
            </span>
          </div>
        </div>

        <!-- Sparklines -->
        <div class="stm-spark">
          <div class="stm-spark-label">{{ t('cards.businessServiceType.servicesPerMonth') }}</div>
          <MonthlyBarChart
            :buckets="sessionBuckets"
            :labels="sparkLabels"
            :tooltip-labels="sparkTooltipLabels"
            :label-stride="sparkLabelStride"
            :width="320"
            :height="36"
            color="var(--color-tufte-muted)"
          />
        </div>
        <div class="stm-spark">
          <div class="stm-spark-label">{{ t('cards.businessServiceType.visitsPerMonth') }}</div>
          <MonthlyBarChart
            :buckets="clientVisitBuckets"
            :labels="sparkLabels"
            :tooltip-labels="sparkTooltipLabels"
            :label-stride="sparkLabelStride"
            :width="320"
            :height="36"
            color="var(--color-money-positive)"
          />
        </div>
        <div class="stm-spark">
          <div class="stm-spark-label">
            {{ t('cards.businessServiceType.uniqueClientsPerMonth') }}
          </div>
          <MonthlyBarChart
            :buckets="uniqueClientsBuckets"
            :labels="sparkLabels"
            :tooltip-labels="sparkTooltipLabels"
            :label-stride="sparkLabelStride"
            :width="320"
            :height="36"
            color="var(--color-gender-men)"
          />
        </div>
        <div class="stm-spark">
          <div class="stm-spark-label">
            {{ t('cards.businessServiceType.clientsPerServicePerMonth') }}
          </div>
          <MonthlyBarChart
            :buckets="clientsPerServiceBuckets"
            :labels="sparkLabels"
            :tooltip-labels="sparkTooltipLabels"
            :label-stride="sparkLabelStride"
            :width="320"
            :height="36"
            color="var(--color-visit-stale)"
          />
        </div>

        <!-- History: fully auto-drained, mirrors desktop card layout -->
        <div class="stm-history">
          <div class="stm-history-header">
            <span>{{ t('cards.businessServiceType.history') }}</span>
            <span v-if="!servicesLoadingDone" class="stm-history-loading">{{
              t('common.loadingShort')
            }}</span>
          </div>
          <template v-if="servicesLoadingDone">
            <div v-if="historyListItems.length === 0" class="stm-history-empty">
              {{ t('cards.businessServiceType.noSessionsRecorded') }}
            </div>
            <template v-for="item in historyListItems" :key="item.id">
              <div v-if="item.kind === 'date-header'" class="stm-history-daygroup">
                <span class="stm-history-daygroup-date">{{ item.date }}</span>
                <span class="stm-history-daygroup-count">{{
                  t('cards.businessServiceType.sessionsCount', item.count)
                }}</span>
              </div>
              <div v-else class="stm-history-row">
                <div class="stm-history-dt">
                  <div class="stm-history-time">{{ item.time }}</div>
                </div>
                <div class="stm-history-performers">
                  <template v-if="item.performers.length > 0">
                    <span
                      v-for="p in item.performers"
                      :key="p.id"
                      class="stm-history-perf"
                      :class="
                        p.gender === 'women' ? 'stm-history-perf--women' : 'stm-history-perf--men'
                      "
                      >{{ p.shortName }}</span
                    >
                  </template>
                  <span v-else class="stm-history-no-perf">–</span>
                </div>
                <div class="stm-history-nums">
                  <span class="stm-history-clients"
                    >{{ item.clientsCount }} (<span class="stm-history-men"
                      >{{ item.menCount }}{{ t('common.maleAbbr') }}</span
                    >/<span class="stm-history-women"
                      >{{ item.womenCount }}{{ t('common.femaleAbbr') }}</span
                    >)</span
                  >
                  <span class="stm-history-rev"
                    >{{ item.revenueLabel ?? '–'
                    }}<span v-if="item.revenueLabel" class="stm-history-cur">{{
                      currencySymbol
                    }}</span></span
                  >
                </div>
              </div>
            </template>
          </template>
        </div>

        <!-- Grows to push Delete to the bottom of the screen when content is
             short; shrinks to 0 and lets the normal scroll take over once
             content overflows the viewport. -->
        <div class="stm-fill" />

        <!-- Delete -->
        <div v-if="canDelete" class="stm-danger-group">
          <button
            type="button"
            class="stm-danger-row"
            :disabled="deleteMutation.isPending.value"
            @click="handleDelete"
          >
            {{ deleteMutation.isPending.value ? t('common.deleting') : t('common.delete') }}
          </button>
        </div>

        <div class="stm-scroll-end" />
      </div>

      <DeleteConfirmSheet
        :open="showDeleteConfirm"
        :title="t('cards.businessServiceType.confirmDeleteTitle')"
        :description="deleteDescription"
        :pending="deleteMutation.isPending.value"
        @confirm="confirmDelete"
        @cancel="cancelDelete"
      />
    </div>
  </Teleport>
</template>

<style scoped>
.stm-screen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10;
  background: #f4f3ea;
  display: flex;
  flex-direction: column;
}

/* ── Navbar ──────────────────────────────────────────────────────────────── */
.stm-navbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 0.6rem 0.75rem 0.5rem;
  border-bottom: 1px solid var(--color-tufte-border);
  background: var(--color-tufte-bg);
}

.stm-nav-back {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font: inherit;
  font-size: 0.9rem;
  color: var(--color-tufte-text);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.2rem 0.3rem;
  flex: 1;
  justify-content: flex-start;
}
.stm-nav-back:active {
  opacity: 0.6;
}

.stm-nav-title {
  font-family: var(--font-tufte-sans);
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  flex: 1;
  text-align: center;
}

.stm-nav-edit {
  font: inherit;
  font-size: 0.9rem;
  text-transform: uppercase;
  color: var(--color-tufte-text);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.2rem 0.3rem;
  flex: 1;
  text-align: right;
}
.stm-nav-edit:disabled {
  color: var(--color-tufte-muted);
  cursor: default;
}
.stm-nav-edit:not(:disabled):active {
  opacity: 0.6;
}

/* ── Loading / error ─────────────────────────────────────────────────────── */
.stm-state {
  padding: 1.5rem 1.25rem;
  font-family: var(--font-tufte-sans);
  font-size: 0.9rem;
  color: var(--color-tufte-muted);
}
.stm-state--error {
  color: var(--color-money-negative);
}

/* ── Scrollable content ──────────────────────────────────────────────────── */
.stm-scroll {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* ── Header ──────────────────────────────────────────────────────────────── */
.stm-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.2rem 1.25rem 1.1rem;
  border-bottom: 1px solid var(--color-tufte-border);
  background: var(--color-tufte-bg);
}

.stm-header-left {
  min-width: 0;
  flex: 1;
}

.stm-name {
  /* --text-tufte-lg, matching the size now used consistently everywhere
   * a business service type's own name is shown. */
  font-size: var(--text-tufte-lg);
  font-weight: 400;
  line-height: 1.1;
  letter-spacing: -0.005em;
}

.stm-duration {
  font-family: var(--font-tufte-sans);
  font-size: 0.74rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  margin-top: 0.35rem;
}

.stm-price {
  flex-shrink: 0;
  text-align: right;
}

.stm-price-amt {
  font-family: var(--font-tufte-mono);
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-money-positive);
  line-height: 1;
}

.stm-price-cur {
  font-size: 0.9rem;
  color: var(--color-tufte-muted);
  margin-left: 0.1rem;
}

.stm-price-per {
  font-family: var(--font-tufte-sans);
  font-size: 0.64rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  margin-top: 0.25rem;
}

/* ── Stat grid ───────────────────────────────────────────────────────────── */
.stm-stat-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-bottom: 1px solid var(--color-tufte-border);
  background: var(--color-tufte-bg);
}

.stm-stat-cell {
  padding: 0.75rem 0.4rem;
  text-align: center;
  border-right: 1px solid var(--color-tufte-border);
}
.stm-stat-cell:last-child {
  border-right: none;
}

.stm-stat-num {
  font-size: 1.4rem;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}
.stm-stat-num--stale {
  color: var(--color-visit-stale);
}

.stm-stat-cap {
  font-family: var(--font-tufte-sans);
  font-size: 0.62rem;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  margin-top: 0.3rem;
}

/* ── Meta strip ──────────────────────────────────────────────────────────── */
.stm-meta-strip {
  padding: 0.65rem 1.25rem;
  border-bottom: 1px solid var(--color-tufte-border);
  background: var(--color-tufte-bg);
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.stm-meta-line {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.stm-meta-label {
  font-family: var(--font-tufte-sans);
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
}

.stm-meta-val {
  font-family: var(--font-tufte-mono);
  font-size: 0.86rem;
}
.stm-meta-val--pos {
  color: var(--color-money-positive);
}

/* ── Sparklines ──────────────────────────────────────────────────────────── */
.stm-spark {
  padding: 0.6rem 1.25rem 0.7rem;
  border-bottom: 1px solid var(--color-tufte-border);
  background: var(--color-tufte-bg);
}

.stm-spark-label {
  font-family: var(--font-tufte-sans);
  font-size: 0.66rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
  margin-bottom: 0.4rem;
}

/* ── History div list ────────────────────────────────────────────────────── */
.stm-history {
  background: var(--color-tufte-bg);
}

.stm-history-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 0.5rem 1.25rem 0.35rem;
  border-bottom: 1px solid var(--color-tufte-border);
  font-family: var(--font-tufte-sans);
  font-size: 0.66rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
}

.stm-history-loading {
  font-family: var(--font-tufte-sans);
  font-size: 0.7rem;
  color: var(--color-tufte-muted);
}

.stm-history-empty {
  padding: 0.75rem 1.25rem;
  font-family: var(--font-tufte-sans);
  font-size: 0.82rem;
  color: var(--color-tufte-muted);
}

/*
 * Sticky per-date divider — the div-list equivalent of `DateHeaderRowCell`,
 * which only works inside a `<table>`'s `<td>` structure. Styled off the
 * same tokens (tinted background, inset border, muted count) so it reads as
 * the same visual language as the ServicesTab/PaymentsTab mobile tables.
 * `position: sticky` targets `.stm-scroll` (the screen's own scroll
 * container) since this list is not independently scrollable.
 */
.stm-history-daygroup {
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 0.3rem 1.25rem;
  background: color-mix(in srgb, var(--color-tufte-bg) 90%, var(--color-money-positive) 10%);
  box-shadow: inset 0 -1px 0 var(--color-tufte-border);
}

.stm-history-daygroup-date {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
}

.stm-history-daygroup-count {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  color: color-mix(in srgb, var(--color-tufte-muted) 60%, var(--color-tufte-bg));
}

.stm-history-row {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  padding: 0.55rem 1.25rem;
  border-bottom: 1px solid #eeeee8;
}
.stm-history-row:last-child {
  border-bottom: none;
}

.stm-history-dt {
  flex-shrink: 0;
  width: 3.4rem;
}

/* Promoted from the muted secondary line it used to be: with the date now
 * shown once per group in `.stm-history-daygroup`, time is this column's
 * only content. */
.stm-history-time {
  font-family: var(--font-tufte-mono);
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--color-tufte-text);
}

.stm-history-performers {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem 0.4rem;
  align-items: baseline;
}

.stm-history-perf {
  font-family: var(--font-tufte-sans);
  font-size: 0.94rem;
  white-space: nowrap;
}
.stm-history-perf--men {
  color: var(--color-gender-men);
}
.stm-history-perf--women {
  color: var(--color-gender-women);
}

.stm-history-no-perf {
  font-family: var(--font-tufte-mono);
  font-size: 0.78rem;
  color: var(--color-tufte-muted);
}

.stm-history-nums {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.1rem;
}

.stm-history-clients {
  font-family: var(--font-tufte-mono);
  font-size: 0.78rem;
  color: var(--color-tufte-text);
}

.stm-history-men {
  color: var(--color-gender-men);
}

.stm-history-women {
  color: var(--color-gender-women);
}

.stm-history-rev {
  font-family: var(--font-tufte-mono);
  font-size: 0.72rem;
  color: var(--color-money-positive);
}

.stm-history-cur {
  font-size: 0.66rem;
  color: var(--color-tufte-muted);
  margin-left: 0.1rem;
}

.stm-fill {
  flex: 1;
}

/* ── Danger group ────────────────────────────────────────────────────────── */
.stm-danger-group {
  margin-top: 1.4rem;
}

.stm-danger-row {
  width: 100%;
  display: block;
  background: var(--color-tufte-bg);
  border: none;
  border-top: 1px solid var(--color-tufte-border);
  border-bottom: 1px solid var(--color-tufte-border);
  padding: 0.85rem 1.25rem;
  text-align: center;
  font: inherit;
  font-size: 0.95rem;
  text-transform: uppercase;
  color: var(--color-tufte-danger);
  cursor: pointer;
}
.stm-danger-row:active {
  background: color-mix(in srgb, var(--color-tufte-danger) 5%, var(--color-tufte-bg));
}
.stm-danger-row:disabled {
  opacity: 0.5;
  cursor: default;
}

.stm-scroll-end {
  height: 2.5rem;
}
</style>
